import { useState } from "react";
import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ChevronRight, Phone } from "lucide-react";
import { useQuoteDrawer } from '../contexts/QuoteDrawerContext';
import { Button } from "@/components/ui/button";
import "./Header.css";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [location] = useLocation();
  const { openDrawer } = useQuoteDrawer();

  const services = [
    { name: "Software Development", href: "/software-development" },
    { name: "Web Design & Development", href: "/web-design-development" },
    { name: "Mobile App Development", href: "/mobile-app-development" },
    { name: "Digital Marketing", href: "/digital-marketing" },
    { name: "eCommerce Solutions", href: "/ecommerce" },
    { name: "Consulting & Strategy", href: "/consulting" },
    { name: "Branding Services", href: "/branding" },
    { name: "UI/UX Design", href: "/ui-ux" },
    { name: "SEO Services", href: "/seo" },
    { name: "Email Hosting", href: "/email-hosting" },
    { name: "Web Hosting", href: "/web-hosting" },
    { name: "Domain Registration", href: "/domain-registration" },
    { name: "ERP / CRM Systems", href: "/erp-crm" },
    { name: "Cloud & DevOps", href: "/cloud-services" },
    { name: "Cyber Security", href: "/cyber-security" },
    { name: "Maintenance & Support", href: "/maintenance-support" },
    { name: "Custom App Development", href: "/custom-apps" },
  ];

  const mobileMenuVariants = {
    open: { 
      x: 0,
      transition: { 
        type: 'spring', 
        stiffness: 300, 
        damping: 40 
      } 
    },
    closed: { 
      x: '100%',
      transition: { 
        type: 'spring', 
        stiffness: 300, 
        damping: 40,
        delay: 0.15
      } 
    }
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
      className="fixed top-0 w-full z-50 bg-gradient-to-r from-background/90 to-muted/10 backdrop-blur-sm border-b border-border/50 py-2"
    >
      <nav className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="group">
            <motion.div
              whileHover={{ 
                scale: 1.05, 
                filter: "drop-shadow(0 0 6px rgba(99, 102, 241, 0.4))"
              }}
              className="flex items-center space-x-3 cursor-pointer"
            >
              <div className="relative">
                <img
                  src="https://res.cloudinary.com/due4nhetn/image/upload/v1750568477/Untitled_design_8_hxwmir.png"
                  alt="ZeeX Soft Logo"
                  className="w-10 h-10 rounded-lg object-cover shadow-lg transition-all duration-300 group-hover:shadow-primary/20"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-orbitron font-bold text-xl bg-clip-text text-transparent bg-[linear-gradient(90deg,var(--primary),var(--primary-foreground))] drop-shadow-[0_0_8px_rgba(99,102,241,0.5)]">ZeeX <span className="text-white">Soft</span></span>
                <span className="text-xs font-medium text-foreground/80 -mt-1 tracking-wider">TECHNOLOGIES</span>
              </div>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1 relative z-10">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <Link 
                    href="/" 
                    className={`px-3 py-1.5 rounded-lg transition-all duration-200 ${location === '/' 
                      ? 'text-primary font-medium relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:rounded-full after:scale-x-100' 
                      : 'text-foreground/90 hover:text-primary'}`}
                  >
                    <span className="relative z-10">Home</span>
                  </Link>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={`group relative font-medium transition-colors duration-200 ${location.startsWith('/services') 
                      ? 'text-primary after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:rounded-full after:scale-x-100' 
                      : 'text-foreground/90 hover:text-primary'}`}
                  >
                    <span className="relative z-10 flex items-center">
                      <span className="mr-1.5">Services</span>
                      <span className="transition-transform duration-200 group-data-[state=open]:rotate-180">
                        <ChevronDown className="h-4 w-4" />
                      </span>
                    </span>
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="p-0">
                    <div className="grid grid-cols-2 gap-1 p-4 w-[600px]">
                      {services.map((service) => (
                        <Link
                          key={service.href}
                          href={service.href}
                          className="group relative flex items-center px-4 py-3 rounded-lg transition-all duration-200 hover:bg-primary/5 hover:text-primary"
                        >
                          <span className="absolute left-0 w-1 h-6 bg-primary rounded-r opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                          <span className="ml-3">{service.name}</span>
                          <ChevronRight className="ml-auto h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        </Link>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link 
                    href="/about" 
                    className={`px-3 py-1.5 rounded-lg transition-all duration-200 ${location === '/about' 
                      ? 'text-primary font-medium relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:rounded-full after:scale-x-100' 
                      : 'text-foreground/90 hover:text-primary'}`}
                  >
                    <span className="relative z-10">About</span>
                  </Link>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <Link 
                    href="/blog" 
                    className={`px-3 py-1.5 rounded-lg transition-all duration-200 ${location === '/blog' 
                      ? 'text-primary font-medium relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:rounded-full after:scale-x-100' 
                      : 'text-foreground/90 hover:text-primary'}`}
                  >
                    <span className="relative z-10">Blog</span>
                  </Link>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <Link 
                    href="/contact" 
                    className={`px-3 py-1.5 rounded-lg transition-all duration-200 ${location === '/contact' 
                      ? 'text-primary font-medium relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:rounded-full after:scale-x-100' 
                      : 'text-foreground/90 hover:text-primary'}`}
                  >
                    <span className="relative z-10">Contact</span>
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Right section with contact info and CTA */}
          <div className="hidden md:flex items-center gap-4">
            {/* Phone number element */}
            <a 
              href="tel:+447743307781"
              className="flex items-center gap-2 text-white hover:text-primary transition-colors group"
            >
              <Phone size={18} className="text-primary group-hover:text-white transition-colors" />
              <span className="font-medium">+44 7743 307781</span>
            </a>

            {/* WhatsApp element */}
            <a 
              href="https://wa.me/447743307781" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-green-500 transition-colors"
              aria-label="Chat on WhatsApp"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.298-.018-.46.13-.608.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.226 1.36.194 1.87.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.652a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </a>

            {/* CTA Button */}
            <Button 
              onClick={(e) => {
                e.preventDefault();
                openDrawer();
              }}
              className="btn-primary animate-glow hover:-translate-y-0.5 transition-transform duration-200"
            >
              Get a Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-primary hover:bg-primary/10 transition-colors duration-200"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={mobileMenuVariants.closed}
              animate={mobileMenuVariants.open}
              exit={mobileMenuVariants.closed}
              className="lg:hidden mt-2 bg-gradient-to-r from-background/90 to-muted/10 backdrop-blur-sm rounded-lg overflow-hidden border border-border/50"
            >
              <div className="p-4 space-y-4 max-h-[80vh] overflow-y-auto">
                <div className="space-y-3">
                  <Link 
                    href="/" 
                    className={`flex items-center px-3 py-2 rounded-lg transition-colors duration-200 ${location === '/' 
                      ? 'text-primary font-medium bg-primary/10' 
                      : 'hover:bg-primary/5 hover:text-primary'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Home
                  </Link>
                  
                  {/* Services Dropdown */}
                  <div className="space-y-2">
                    <button 
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors duration-200 ${mobileServicesOpen 
                        ? 'text-primary font-medium bg-primary/10' 
                        : 'hover:bg-primary/5 hover:text-primary'}`}
                    >
                      <span>Our Services</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    <div className={`pl-4 space-y-1 overflow-hidden transition-all duration-200 ${mobileServicesOpen ? 'max-h-[1000px]' : 'max-h-0'}`}>
                      <div className="grid grid-cols-1 gap-1 py-2">
                        {services.map((service) => (
                          <Link
                            key={service.href}
                            href={service.href}
                            className="flex items-center px-3 py-2 text-sm rounded-lg hover:bg-primary/5 hover:text-primary transition-colors duration-200"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <span className="flex-1">{service.name}</span>
                            <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <Link 
                    href="/about" 
                    className={`flex items-center px-3 py-2 rounded-lg transition-colors duration-200 ${location === '/about' 
                      ? 'text-primary font-medium bg-primary/10' 
                      : 'hover:bg-primary/5 hover:text-primary'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    About Us
                  </Link>
                  
                  <Link 
                    href="/blog" 
                    className={`flex items-center px-3 py-2 rounded-lg transition-colors duration-200 ${location === '/blog' 
                      ? 'text-primary font-medium bg-primary/10' 
                      : 'hover:bg-primary/5 hover:text-primary'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Blog
                  </Link>
                  
                  <Link 
                    href="/contact" 
                    className={`flex items-center px-3 py-2 rounded-lg transition-colors duration-200 ${location === '/contact' 
                      ? 'text-primary font-medium bg-primary/10' 
                      : 'hover:bg-primary/5 hover:text-primary'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Contact Us
                  </Link>
                </div>
                
                <div className="pt-2">
                  <Button 
                    onClick={(e) => { 
                      e.preventDefault();
                      openDrawer(); 
                    }} 
                    className="w-full btn-primary hover:-translate-y-0.5 transition-transform duration-200"
                  >
                    Get Free Quote
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
}
